import { Component, OnDestroy, inject } from '@angular/core';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { FrequencyDrawerParams } from '../../insights-data.interface';
import { InsightsDataService } from '../../insights-data.service';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { AuditContextService } from '../../../../../audit-logs/audit-context.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import _ from 'lodash';
import { EnterDataSaveNextUtility } from '../enter-data-save-next.utility';
@Component({
  selector: 'app-partial-interval',
  standalone: true,
  imports: [
    NzSpinModule,
    NzButtonModule,
    NzInputModule,
    CommonModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormModule,
    NgxMaskDirective,
    ReactiveFormsModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './partial-interval.component.html',
  styleUrl: './partial-interval.component.scss',
})
export class PartialIntervalComponent implements OnDestroy {
  private readonly auditContextService = inject(AuditContextService);
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  form!: FormGroup;
  loading$: Observable<boolean> = of(false);
  constructor(
    private readonly drawerService: DrawerService,
    private readonly fb: FormBuilder,
    private readonly loaderService: LoaderService,
    private readonly insightsService: InsightsDataService,
    private readonly commonService: CommonService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group({
      id: [0],
      dataDate: [this.drawerParmas.selectedDate],
      isIntervalFrequency: [true],
      isDuration: [false],
      interval1: [''],
      interval2: [''],
      interval3: [''],
    });
    this.setupDrawerSubscription();
  }
  // Getter function to get unitValue dynamically
  get unitValue(): string {
    return [
      this.form.value.interval1,
      this.form.value.interval2,
      this.form.value.interval3,
    ].join(','); // Join values with comma
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.PARTIAL_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          this.form.reset({
            id: 0,
            isIntervalFrequency: true,
            isDuration: false,
            interval1: '',
            interval2: '',
            interval3: '',
          });
          const matchedData = _.find(
            this.drawerParmas.behaviorData.activityDataUnitMultiValues,
            { dataDate: this.drawerParmas.selectedDate }
          );
          if (matchedData) {
            const unitValueArray = matchedData.unitValue
              .split(',')
              .map(val => (val.trim() === '' ? null : Number(val.trim())));
            this.form.patchValue({
              id: matchedData.id,
              interval1: unitValueArray[0],
              interval2: unitValueArray[1],
              interval3: unitValueArray[2],
            });
          }
        }
      });
  }

  private getNextWorkingDate(): string | null {
    if (!this.drawerParmas?.selectedDate) return null;
    return EnterDataSaveNextUtility.getNextWorkingDate(
      this.drawerParmas.selectedDate
    );
  }

  get hasNextDate(): boolean {
    return (
      this.drawerParmas.selectedUnit !== 1 && this.getNextWorkingDate() !== null
    );
  }

  submitAndNext(): void {
    const matchedData = _.find(
      this.drawerParmas.behaviorData.activityDataUnitMultiValues,
      { dataDate: this.drawerParmas.selectedDate }
    );
    const original = { unitValue: matchedData ? matchedData.unitValue : '' };
    const updated = { unitValue: this.unitValue };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: [],
        labelMap: {
          unitValue: `${this.drawerParmas.selectedUnitName ?? ''} (${this.drawerParmas.selectedDate})`,
        },
        excludeFields: [],
      }
    );
    const payload = {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: [
        {
          id: this.form.value.id,
          dataDate: this.drawerParmas.selectedDate,
          startDataTime: '00:00:01',
          endDataTime: '00:00:01',
          unitValue: this.unitValue,
          isIntervalFrequency: true,
          isDuration: false,
        },
      ],
      _modifiedFields: modifiedFields,
    };
    this.loaderService.show();
    this.insightsService
      .saveMultiValue(payload)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', 'Data saved successfully.');
          const nextDate = this.getNextWorkingDate();
          if (nextDate) {
            const updatedParams: FrequencyDrawerParams = {
              ...this.drawerParmas,
              selectedDate: nextDate,
              selectedDay: EnterDataSaveNextUtility.getDayName(nextDate),
              selectedRow: {
                id: 0,
                date: nextDate,
                day: EnterDataSaveNextUtility.getDayName(nextDate),
                individualIntervalDetails: '',
                totalFrequency: '',
                totalDuration: '',
                averageDuration: '',
                action: '',
                isFrequencyValue: false,
                isDuration: false,
                isUpdateTotal: false,
                isFrequencyOnly: false,
                commentCount: 0,
              },
            };
            this.drawerService.setParams(
              DRAWER_IDS.PARTIAL_INTERVAL,
              updatedParams
            );
          }
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  get isIntervalFormValid() {
    return [
      this.form.value.interval1,
      this.form.value.interval2,
      this.form.value.interval3,
    ].some(val => val !== null && val !== '');
  }

  submitForm() {
    const matchedData = _.find(
      this.drawerParmas.behaviorData.activityDataUnitMultiValues,
      { dataDate: this.drawerParmas.selectedDate }
    );
    const original = { unitValue: matchedData ? matchedData.unitValue : '' };
    const updated = { unitValue: this.unitValue };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: [],
        labelMap: {
          unitValue: `${this.drawerParmas.selectedUnitName ?? ''} (${this.drawerParmas.selectedDate})`,
        },
        excludeFields: [],
      }
    );
    const payload = {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: [
        {
          id: this.form.value.id,
          dataDate: this.drawerParmas.selectedDate,
          startDataTime: '00:00:01',
          endDataTime: '00:00:01',
          unitValue: this.unitValue,
          isIntervalFrequency: true,
          isDuration: false,
        },
      ],
      _modifiedFields: modifiedFields,
    };
    this.loaderService.show();
    const operation$ = this.insightsService.saveMultiValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', `Data saved successfully.`);
          this.onCancel();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.PARTIAL_INTERVAL);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validateInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement; // Explicitly cast event.target
    const value = inputElement.value.trim();

    // Convert to a number
    const num = Number.parseInt(value, 10);

    // If input is not a number or out of range, clear it
    if (Number.isNaN(num) || num < 0 || num > 10) {
      inputElement.value = '';
    }
  }
}
